import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, Check, Flame, Delete, Volume2 } from "lucide-react";
import { useGameRound } from "./useGameRound";
import ResultsOverlay from "../components/ResultsOverlay";
import MascotCoach from "../components/MascotCoach";
import { MASCOTS, pick, TARGET_SKINS } from "../data/gameData";
import { useProfile } from "../context/ProfileContext";
import { sound } from "../lib/sound";
import { speech } from "../lib/speech";
import { questionFor } from "./questionModes";

export default function MultipleChoiceGame({ game, tables, subtitle }) {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { readAloud } = useProfile();
  const r = useGameRound({
    gameType: game.id, tables,
    count: game.count || 10, choices: game.choices || 4,
  });
  const accent = game.color;
  const mascot = MASCOTS[game.mascot] || MASCOTS.lumi;
  const [coachMsg, setCoachMsg] = useState(mascot.welcome[0]);
  const [typed, setTyped] = useState("");
  const [special, setSpecial] = useState(false);
  const skin = TARGET_SKINS[r.profile?.equipped?.target] || TARGET_SKINS.target_neon;
  const question = r.current ? questionFor(game.id, r.current, r.index) : null;
  const sceneAnswers = ["targets", "doors", "orbs"].includes(game.answerStyle);

  const isTyped = useMemo(() => {
    if (!r.current) return false;
    if (game.answerStyle !== "pad") return false;
    return (r.current.state === "fluent" || r.current.state === "mastered") && r.index % 4 === 3;
  }, [r.current, r.index, game.answerStyle]);

  useEffect(() => { setTyped(""); }, [r.index]);

  useEffect(() => {
    if (!r.feedback) return;
    if (r.feedback.correct) {
      sound.correct();
      if (r.streak > 0 && r.streak % 3 === 0) {
        sound.special();
        setSpecial(true);
        setCoachMsg(pick(mascot.milestone));
        setTimeout(() => setSpecial(false), 950);
      } else {
        setCoachMsg(pick(mascot.correct));
      }
    } else {
      sound.wrong();
      setCoachMsg(pick(mascot.wrong));
    }
  }, [r.feedback]);

  useEffect(() => {
    if (readAloud && question) speech.speak(question.expression.replace(/×/g, " times ").replace(/÷/g, " divided by ").replace("?", "what number"));
    return () => speech.cancel();
  }, [r.index, readAloud, r.current]);

  useEffect(() => { if (r.finished) sound.win(); }, [r.finished]);

  if (r.loading || !r.current) {
    return (
      <div className="mr-bg min-h-screen flex items-center justify-center">
        <div className="text-white/70 font-bold animate-pulse">Loading {game.name}…</div>
      </div>
    );
  }

  const progressPct = Math.round((r.index / r.total) * 100);
  const correctPct = Math.round((r.score / r.total) * 100);

  const optState = (opt) => {
    if (!r.feedback) return "idle";
    if (opt === r.feedback.correctAnswer) return "correct";
    if (opt === r.feedback.chosen) return "wrong";
    return "dim";
  };

  const submitTyped = () => {
    if (typed === "") return;
    r.answer(Number(typed), question.answer);
  };

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{ background: `radial-gradient(900px 500px at 50% -10%, ${accent}33, transparent 60%), linear-gradient(180deg,#0A0B1A,#12091F 60%,#0A0B1A)` }}
         data-testid="game-screen">
      {/* Header */}
      <div className="relative z-20 px-4 pt-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button data-testid="game-exit-btn" onClick={() => navigate("/map")}
                  className="w-10 h-10 rounded-full glass flex items-center justify-center press">
            <X size={20} />
          </button>
          <div className="flex-1">
            <div className="text-sm font-bold leading-none">{game.name}</div>
            <div className="text-[11px] text-[#A8B2D1]">{subtitle}</div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass font-bold text-sm"
               data-testid="game-streak">
            <Flame size={15} className="text-[#FF7A00]" /> {r.streak}
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full transition-all"
               style={{ width: `${progressPct}%`, background: accent }} />
        </div>
        <div className="max-w-5xl mx-auto mt-1 text-[11px] text-[#A8B2D1] text-right">
          Question {Math.min(r.index + 1, r.total)} / {r.total}
        </div>
      </div>

      {/* Equation */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 mt-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={r.index}
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="glass-dark rounded-3xl py-4 sm:py-5 text-center relative"
            data-testid="equation-panel">
            <button
              data-testid="speak-btn"
              onClick={() => speech.speak(question.expression.replace(/×/g, " times ").replace(/÷/g, " divided by ").replace("?", "what number"))}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/8 flex items-center justify-center press">
              <Volume2 size={17} style={{ color: accent }} />
            </button>
            <div className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: accent }}>{question.label}</div>
            <div className="font-math font-extrabold text-3xl sm:text-5xl tracking-normal"
                 data-testid="equation-text">
              {question.expression}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* The question leads into a full-size interactive scene. */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 mt-3">
        <Stage game={game} accent={accent} correctPct={correctPct} score={r.score}
               total={r.total} index={r.index} feedback={r.feedback} reduce={reduce}
               choices={sceneAnswers ? <Answers style={game.answerStyle} options={question.options}
                    accent={accent} skin={skin} optState={optState}
                    onPick={(value) => r.answer(value, question.answer)} locked={r.locked} reduce={reduce} /> : null} />
      </div>

      {/* Answers */}
      {!sceneAnswers && <div className="relative z-10 max-w-5xl mx-auto px-4 mt-4 pb-40">
        {isTyped ? (
          <TypedPad typed={typed} setTyped={setTyped} onSubmit={submitTyped}
                    feedback={r.feedback} accent={accent} correct={question.answer} locked={r.locked} />
        ) : (
          <Answers style={game.answerStyle} options={question.options} accent={accent} skin={skin}
                   optState={optState} onPick={(value) => r.answer(value, question.answer)} locked={r.locked} reduce={reduce} />
        )}
      </div>}

      {/* Mascot */}
      <div className="fixed bottom-4 left-4 z-30">
        <MascotCoach mascotId={game.mascot} message={coachMsg} color={accent} />
      </div>

      {/* Special-move burst */}
      <AnimatePresence>
        {special && (
          <motion.div
            className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="special-burst">
            <motion.div
              initial={{ scale: 0.3, rotate: -12 }} animate={{ scale: 1.1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 14 }}
              className="text-center">
              <div className="font-black text-5xl sm:text-6xl drop-shadow-[0_0_30px_rgba(255,214,0,0.8)]"
                   style={{ color: accent }}>
                {game.stage === "boss" ? "SPECIAL MOVE!" : `${r.streak} COMBO!`}
              </div>
              {!reduce && (
                <div className="flex justify-center gap-2 mt-2">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <motion.span key={i} className="text-3xl"
                      initial={{ y: 0, opacity: 1 }} animate={{ y: -60 - i * 8, opacity: 0 }}
                      transition={{ duration: 0.9, delay: i * 0.04 }}>✦</motion.span>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {r.finished && (
          <ResultsOverlay result={r.result} accent={accent}
                          onReplay={r.restart} onExit={() => navigate("/map")} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ----------------------------- Answers ----------------------------- */
function Answers({ style, options, accent, skin, optState, onPick, locked, reduce }) {
  if (style === "targets") return <Targets {...{ options, accent, skin, optState, onPick, locked, reduce }} />;
  if (style === "doors") return <Doors {...{ options, accent, skin, optState, onPick, locked }} />;
  if (style === "orbs") return <Orbs {...{ options, accent, skin, optState, onPick, locked, reduce }} />;
  return <Pad {...{ options, accent, skin, optState, onPick, locked }} />;
}

const stateStyle = (st, accent) => {
  if (st === "correct") return { background: "rgba(0,255,102,0.22)", borderColor: "#00FF66", color: "#eafff1" };
  if (st === "wrong") return { background: "rgba(255,51,102,0.22)", borderColor: "#FF3366", color: "#ffe6ec" };
  if (st === "dim") return { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)", opacity: 0.45 };
  return { background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.16)" };
};

function OptIcon({ st }) {
  if (st === "correct") return <Check size={20} className="text-[#00FF66]" />;
  if (st === "wrong") return <X size={20} className="text-[#FF3366]" />;
  return null;
}

function Pad({ options, accent, skin, optState, onPick, locked }) {
  return (
    <div className="grid grid-cols-2 gap-3 max-w-3xl mx-auto" data-testid="answers-pad">
      {options.map((opt, i) => {
        const st = optState(opt);
        const base = stateStyle(st, accent);
        if (st === "idle" && skin) {
          base.borderColor = `${skin.color}88`;
          base.boxShadow = `0 0 16px ${skin.color}33`;
          base.borderRadius = skin.radius === "9999px" ? "1.5rem" : skin.radius;
        }
        return (
          <button
            key={i}
            data-testid={`answer-${opt}`}
            disabled={locked}
            onClick={() => onPick(opt)}
            className="press border-2 rounded-3xl min-h-24 flex items-center justify-center gap-2 relative"
            style={base}>
            <span className="font-math font-extrabold text-3xl sm:text-4xl">{opt}</span>
            <span className="absolute top-2 right-3"><OptIcon st={st} /></span>
          </button>
        );
      })}
    </div>
  );
}

function Targets({ options, accent, skin, optState, onPick, locked, reduce }) {
  const glow = skin?.color || accent;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 place-items-center py-2" data-testid="answers-targets">
      {options.map((opt, i) => {
        const st = optState(opt);
        return (
          <motion.button
            key={i}
            data-testid={`answer-${opt}`}
            disabled={locked}
            onClick={() => onPick(opt)}
            animate={reduce ? {} : { y: [0, -8, 0] }}
            transition={{ duration: 3 + (i % 3), repeat: Infinity, ease: "easeInOut" }}
            whileTap={{ scale: 0.88 }}
            className="relative w-28 h-28 sm:w-36 sm:h-36 border-4 flex items-center justify-center overflow-hidden"
            style={{
              ...stateStyle(st, accent),
              borderRadius: skin?.radius || "9999px",
              boxShadow: st === "idle" ? `0 0 22px ${glow}66, inset 0 0 18px ${glow}44` : "none",
              borderColor: st === "idle" ? glow : stateStyle(st, accent).borderColor,
            }}>
            <span className="absolute inset-3 rounded-full border-2 border-white/30" />
            <span className="absolute inset-7 rounded-full border-2 border-white/30" />
            <span className="relative font-math font-extrabold text-2xl sm:text-3xl drop-shadow-lg">{opt}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

function Doors({ options, accent, skin, optState, onPick, locked }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="answers-doors">
      {options.map((opt, i) => {
        const st = optState(opt);
        const open = st === "correct" || st === "wrong";
        const base = stateStyle(st, accent);
        if (st === "idle" && skin) base.borderColor = `${skin.color}88`;
        return (
          <button
            key={i}
            data-testid={`answer-${opt}`}
            disabled={locked}
            onClick={() => onPick(opt)}
            className="press rounded-t-[2rem] rounded-b-lg border-2 border-b-8 h-36 sm:h-52 flex flex-col items-center justify-center relative overflow-hidden"
            style={{ ...base, background: st === "idle" ? "linear-gradient(140deg,#946025,#352333 72%)" : base.background }}>
            <div className="absolute inset-2 rounded-t-[1.5rem] rounded-b-sm border border-amber-200/35" />
            <div className="absolute right-3 sm:right-5 top-1/2 w-3 h-3 rounded-full bg-[#FFD600] shadow-[0_0_10px_#FFD600]" />
            <span className="font-math font-extrabold text-2xl sm:text-4xl mt-3 relative">{opt}</span>
            {open && <div className="absolute bottom-2"><OptIcon st={st} /></div>}
          </button>
        );
      })}
    </div>
  );
}

function Orbs({ options, accent, skin, optState, onPick, locked, reduce }) {
  const glow = skin?.color || accent;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 place-items-center gap-4 py-2" data-testid="answers-orbs">
      {options.map((opt, i) => {
        const st = optState(opt);
        const sliced = st === "correct" || st === "wrong";
        return (
          <motion.button
            key={i}
            data-testid={`answer-${opt}`}
            disabled={locked}
            onClick={() => onPick(opt)}
            animate={reduce ? {} : { rotate: [0, 6, -6, 0], y: [0, -6, 0] }}
            transition={{ duration: 4 + (i % 3), repeat: Infinity, ease: "easeInOut" }}
            whileTap={{ scale: 0.8, rotate: 20 }}
            className="w-28 h-28 sm:w-36 sm:h-36 rounded-[38%] border-4 flex items-center justify-center relative"
            style={{ ...stateStyle(st, accent),
                     borderColor: st === "idle" ? glow : stateStyle(st, accent).borderColor,
                     boxShadow: st === "idle" ? `0 0 20px ${glow}55` : "none",
                     opacity: sliced && st === "dim" ? 0.3 : 1 }}>
            <span className="absolute inset-2 rounded-[40%] border border-white/35 -rotate-12" />
            <span className="relative font-math font-extrabold text-2xl sm:text-3xl">{opt}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ----------------------------- Typed pad ----------------------------- */
function TypedPad({ typed, setTyped, onSubmit, feedback, accent, correct, locked }) {
  useEffect(() => {
    const h = (e) => {
      if (locked) return;
      if (e.key >= "0" && e.key <= "9") setTyped((t) => (t.length < 4 ? t + e.key : t));
      else if (e.key === "Backspace") setTyped((t) => t.slice(0, -1));
      else if (e.key === "Enter") onSubmit();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [locked, onSubmit, setTyped]);

  const st = feedback ? (feedback.correct ? "correct" : "wrong") : "idle";
  return (
    <div data-testid="answers-typed">
      <div className="text-center text-xs text-[#A8B2D1] mb-2 font-bold">Type your answer</div>
      <div className="glass-dark rounded-2xl py-4 mb-3 text-center border-2"
           style={{ borderColor: feedback ? stateStyle(st).borderColor : accent }}>
        <span className="font-math font-extrabold text-4xl">{typed || "_"}</span>
        {feedback && !feedback.correct && (
          <span className="ml-3 font-math text-2xl text-[#00FF66]">→ {correct}</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} data-testid={`key-${n}`} disabled={locked}
                  onClick={() => setTyped((t) => (t.length < 4 ? t + n : t))}
                  className="press glass rounded-2xl py-4 font-math font-bold text-2xl">{n}</button>
        ))}
        <button data-testid="key-clear" disabled={locked} onClick={() => setTyped("")}
                className="press glass rounded-2xl py-4 flex items-center justify-center">
          <Delete size={22} />
        </button>
        <button data-testid="key-0" disabled={locked}
                onClick={() => setTyped((t) => (t.length < 4 ? t + "0" : t))}
                className="press glass rounded-2xl py-4 font-math font-bold text-2xl">0</button>
        <button data-testid="key-enter" disabled={locked || typed === ""} onClick={onSubmit}
                className="press btn-3d rounded-2xl py-4 font-bold text-white disabled:opacity-40"
                style={{ background: accent }}>OK</button>
      </div>
    </div>
  );
}

/* ----------------------------- Stage visuals ----------------------------- */
function Stage({ game, accent, correctPct, score, total, index, feedback, reduce, choices }) {
  const kind = game.stage;
  if (kind === "racer") {
    return (
      <div className="glass rounded-3xl relative overflow-hidden h-52 sm:h-72 border border-cyan-400/30"
           style={{ background: "radial-gradient(circle at 75% 20%, #0a557060, transparent 42%), linear-gradient(#171e41, #102235 62%, #16222b)" }}
           data-testid="stage-racer">
        <div className="absolute top-4 left-5 text-xs font-black uppercase tracking-[0.2em] text-cyan-200">Neon circuit · Lap {index + 1}/{total}</div>
        <div className="absolute top-5 right-5 text-lg font-black text-cyan-200">{Math.round(index / total * 100)}%</div>
        <div className="absolute inset-x-0 bottom-7 sm:bottom-10 h-20 sm:h-28 bg-slate-900/70 border-y-4 border-cyan-300/50" />
        <div className="absolute inset-x-0 bottom-16 sm:bottom-[5.5rem] border-t-4 border-dashed border-white/40" />
        <div className="absolute right-4 sm:right-10 bottom-20 sm:bottom-28 text-5xl sm:text-7xl drop-shadow-lg">🏁</div>
        <motion.div className="absolute bottom-10 sm:bottom-12 text-6xl sm:text-8xl drop-shadow-[0_0_24px_#00e5ff]"
          animate={{ left: `${Math.min(80, 4 + (index / total) * 76)}%`,
                     x: feedback?.correct && !reduce ? [0, 20, 0] : 0 }}
          transition={{ type: "spring", stiffness: 120 }}>🏎️</motion.div>
      </div>
    );
  }
  if (kind === "monster" || kind === "boss") {
    const hp = Math.max(0, 100 - correctPct);
    const boss = kind === "boss";
    const phase = Math.min(3, Math.floor((correctPct / 100) * 3) + 1);
    return (
      <div className="glass rounded-3xl p-5 relative overflow-hidden h-52 sm:h-72 flex flex-col justify-between"
           style={{ background: `radial-gradient(circle at 72% 60%, ${accent}55, transparent 44%),linear-gradient(135deg,#12102a,#201331)` }}
           data-testid={`stage-${kind}`}>
        <div className="relative z-10 flex items-center justify-between gap-4">
          <span className="text-xs sm:text-base font-black uppercase tracking-widest">{boss ? "Boss arena" : "Monster battle"}</span>
          <div className="w-32 sm:w-56">
            <div className="flex justify-between items-center text-xs text-[#A8B2D1] mb-1">
              {boss ? (
                <AnimatePresence mode="wait">
                  <motion.span key={phase}
                    initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="font-black text-[11px] px-2 py-0.5 rounded-full"
                    style={{ background: "#F43F5E22", color: "#ff8fa3" }}
                    data-testid="boss-phase">PHASE {phase}/3</motion.span>
                </AnimatePresence>
              ) : <span>Monster HP</span>}
              <span>{hp}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/10 overflow-hidden">
              <motion.div className="h-full rounded-full"
                animate={{ width: `${hp}%` }} transition={{ type: "spring", stiffness: 120 }}
                style={{ background: "linear-gradient(90deg,#FF3366,#FF7A00)" }} />
            </div>
          </div>
        </div>
        <div className="relative z-10 flex items-end justify-between px-2 sm:px-12">
          <motion.span className="text-7xl sm:text-9xl drop-shadow-[0_0_24px_#00e5ff]"
            animate={feedback?.correct && !reduce ? { x: [0, 24, 0] } : { x: 0 }}>🦸</motion.span>
          <div className="flex-1 h-1 mx-4 sm:mx-10 mb-10 rounded-full"
               style={{ background: `linear-gradient(90deg,${accent},transparent)` }} />
          <motion.span className="text-7xl sm:text-9xl drop-shadow-[0_0_24px_#f43f5e]"
            animate={feedback?.correct && !reduce
              ? { scale: [1, 1.35, 1], rotate: [0, -10, 0], filter: ["brightness(1)", "brightness(1.8)", "brightness(1)"] }
              : (boss ? { scale: 1 + phase * 0.12 } : {})}>
            {boss ? "👹" : "👾"}
          </motion.span>
        </div>
      </div>
    );
  }
  if (kind === "tower") {
    const blocks = score;
    const colors = ["#FF2A7A", "#00E5FF", "#FFD600", "#00FF66", "#A855F7"];
    return (
      <div className="glass rounded-3xl p-4 flex flex-col items-center justify-between h-60 sm:h-72 overflow-hidden"
           style={{ background: "linear-gradient(#0d2930,#172a36 75%,#15534e)" }}
           data-testid="stage-tower">
        <div className="self-start text-xs sm:text-base font-black uppercase tracking-widest">Skyline tower · {blocks} floors</div>
        <div className="flex flex-col-reverse items-center gap-1 w-full mb-1">
          <div className="w-40 sm:w-60 h-3 bg-white/40 rounded-full" />
          {Array.from({ length: Math.min(blocks, 10) }).map((_, i) => (
            <motion.div key={i} initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              className="w-32 sm:w-52 h-3 sm:h-5 rounded border border-white/40 shadow-lg"
              style={{ background: colors[i % colors.length], boxShadow: `0 0 14px ${colors[i % colors.length]}77` }} />
          ))}
          {blocks === 0 && <div className="text-sm text-white/70 mb-8">Each correct answer adds a glowing floor!</div>}
        </div>
      </div>
    );
  }
  if (kind === "targets" || kind === "ninja" || kind === "doors") {
    return (
      <div className="glass rounded-3xl relative overflow-hidden min-h-72 sm:min-h-80 p-5 sm:p-8 flex flex-col justify-center"
           style={{ background: kind === "ninja" ? "radial-gradient(circle at center,#17495d,#081924 75%)" : kind === "doors" ? "radial-gradient(circle at center,#5a331b,#20121a 75%)" : "radial-gradient(circle at center,#472044,#180e30 75%)" }}
           data-testid={`stage-${kind}`}>
        <div className="text-center text-xs sm:text-base font-bold uppercase tracking-widest mb-5" style={{ color: accent }}>
          {kind === "ninja" ? "Slice the glowing answer" : kind === "doors" ? "Choose the treasure door" : "Tap the target to blast it"}
        </div>
        {choices}
        <div className="text-center text-xs text-white/60 font-bold mt-5">{score} correct · keep your combo alive</div>
      </div>
    );
  }
  return null;
}
